/**
 * DeleteWatchlistModal — confirmation dialog for deleting the currently-selected
 * user watchlist.  Calls `deleteUserWatchlist()` on confirm.
 */
import Modal, { Styles } from "react-modal";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { HiOutlineTrash } from "react-icons/hi";
import { getColorTheme } from "@service/local-storage-handler";
import { deleteUserWatchlist } from "@service/watchlist-handler";

import "../watchlist-modal-styles.css";

Modal.setAppElement("#marketview");

const DeleteWatchlistModal = ({
  deleteWatchlistModalIsOpen,
  setDeleteWatchlistModalIsOpen,
}) => {
  const { selectedWatchlist } = useSelector(({ watchlist }) => watchlist);

  const themeFromStore = useSelector(({ theme }) => theme.theme);
  // Default to dark if theme is not set in Redux (fallback to localStorage)
  const theme = themeFromStore || getColorTheme() || "dark";

  const closeModal = () => {
    setDeleteWatchlistModalIsOpen(false);
  };

  const handleNoButton = () => {
    closeModal();
  };

  const handleYesButton = (event) => {
    event.preventDefault();
    deleteUserWatchlist(selectedWatchlist.value)
      .then(() => {
        toast.success(
          (t) => (
            <div className={`toast-msgs-container ${theme}`}>
              <div>
                <label
                  htmlFor="toast-msg"
                  style={{ color: theme === "dark" ? "#f7fafc" : "#1a202c" }}
                >
                  Watchlist deleted successfully
                </label>
              </div>
              <div>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          {
            duration: 6000,
            style: {
              fontSize: "0.8rem",
              borderRadius: 0,
              textAlign: "left",
              paddingRight: 0,
            },
          }
        );
      })
      .catch((error) => {
        toast.error(
          (t) => (
            <div className={`toast-msgs-container ${theme}`}>
              <div>
                <label
                  htmlFor="toast-msg"
                  style={{ color: theme === "dark" ? "#f7fafc" : "#1a202c" }}
                >
                  {`Error while deleting watchlist. ${error}`}
                </label>
              </div>
              <div>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          {
            duration: 6000,
            style: {
              fontSize: "0.8rem",
              borderRadius: 0,
              textAlign: "left",
              paddingRight: 0,
            },
          }
        );
      })
      .finally(() => {
        closeModal();
      });
  };

  return (
    <>
      <Modal
        isOpen={deleteWatchlistModalIsOpen}
        onRequestClose={() => setDeleteWatchlistModalIsOpen(false)}
        style={getModalStyles(theme)}
      >
        <div className={`wl-modal ${theme}`}>
          {/* Coloured header banner */}
          <div className="wl-modal__header wl-modal__header--delete">
            <div className="wl-modal__header-bg wl-modal__header-bg--delete" />
            <div className="wl-modal__icon-wrap">
              <div className="wl-modal__icon">
                <HiOutlineTrash />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="wl-modal__body">
            <h3 className="wl-modal__title">Delete Watchlist</h3>
            <div className="wl-modal__divider" />
            <p className="wl-modal__desc">
              Are you sure you want to delete{" "}
              <span className="wl-modal__highlight--danger">{selectedWatchlist?.label}</span>{" "}
              watchlist? This action cannot be undone.
            </p>
          </div>

          {/* Footer */}
          <div className="wl-modal__footer">
            <button type="button" className="wl-modal__btn wl-modal__btn--cancel" onClick={handleNoButton}>
              Cancel
            </button>
            <button type="button" className="wl-modal__btn wl-modal__btn--delete" onClick={handleYesButton}>
              Yes, Delete
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DeleteWatchlistModal;

const getModalStyles = (theme: string): Styles => ({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme === "dark" ? "rgba(0,0,0,0.75)" : "rgba(15,23,42,0.55)",
    zIndex: 10001,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  },
  content: {
    position: "relative",
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    border: "none",
    background: "transparent",
    overflow: "visible",
    WebkitOverflowScrolling: "touch",
    borderRadius: "0",
    outline: "none",
    padding: "0",
    maxWidth: "400px",
    width: "90%",
  },
});
